import * as i3 from '@angular/common';
import { DOCUMENT, NgComponentOutlet, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, TemplateRef, Component, ViewEncapsulation, ChangeDetectionStrategy, Optional, Inject, ViewChild, Injector, Injectable, ElementRef, Input, Output, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { state, style, transition, animate, trigger } from '@angular/animations';
import * as i1 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i2 from '@rogers/cdk/common/services';
import { __decorate } from 'tslib';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Required } from '@rogers/cdk/common/decorators';
import { ModalEvents } from '@rogers/cdk/common/enums';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { ModalInjectableData } from '@rogers/cdk/common/classes';
import { filter, take, first } from 'rxjs/operators';
import { ESCAPE } from '@angular/cdk/keycodes';
import * as i4 from '@rogers/cdk/icon';
import { IconModule } from '@rogers/cdk/icon';
import * as i5 from '@rogers/cdk/button';
import { ButtonModule } from '@rogers/cdk/button';

const animationBody = [
    state('void, exit', style({ opacity: 0, transform: 'scale(0.7)' })),
    state('enter', style({ transform: 'none' })),
    transition('* => enter', animate('300ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 }))),
    transition('* => void, * => exit', animate('75ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0 }))),
];
const dsModalAnimations = {
    modalContainer: trigger('modalContainer', animationBody),
};

class ModalContainerComponent {
    constructor(elementRef, focusTrapFactory, changeDetectorRef, windowService, document) {
        this.elementRef = elementRef;
        this.focusTrapFactory = focusTrapFactory;
        this.changeDetectorRef = changeDetectorRef;
        this.windowService = windowService;
        this.document = document;
        this.elementFocusedBeforeDialogWasOpened = null;
        this.state = 'enter';
        this.outletData = {
            content: null,
            context: null,
            injector: null,
        };
        this.animationStateChanged = new EventEmitter();
        this.destroyedAbnormally$ = new Subject();
        this.isAppleMobileDevice = false;
    }
    get uniqueId() {
        return this.id.split('-').reverse()[0];
    }
    ngOnInit() { }
    ngAfterContentInit() {
        this.isAppleMobileDevice = this.windowService.isAppleMobileDevice()
            ? true
            : false;
        this.changeDetectorRef.markForCheck();
    }
    isTemplate() {
        return this.outletData.content instanceof TemplateRef;
    }
    attach(outletData) {
        this.outletData = outletData;
        this.changeDetectorRef.detectChanges();
        const compRef = this.componentOutlet
            ?._componentRef;
        return compRef?.instance;
    }
    trapFocus() {
        if (!this.focusTrap) {
            this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
            this.focusTrap.focusFirstTabbableElementWhenReady();
        }
    }
    restoreFocus() {
        const toFocus = this.elementFocusedBeforeDialogWasOpened;
        if (toFocus && typeof toFocus.focus === 'function') {
            toFocus.focus();
        }
        if (this.focusTrap) {
            this.focusTrap.destroy();
        }
    }
    savePreviouslyFocusedElement() {
        if (this.document) {
            this.elementFocusedBeforeDialogWasOpened = this.document
                .activeElement;
            if (this.elementRef.nativeElement.focus) {
                Promise.resolve().then(() => this.elementRef.nativeElement.focus());
            }
        }
    }
    onAnimationDone(event) {
        if (event.toState === 'enter') {
            this.savePreviouslyFocusedElement();
            this.trapFocus();
        }
        else if (event.toState === 'exit') {
            this.restoreFocus();
        }
        this.animationStateChanged.emit(event);
    }
    onAnimationStart(event) {
        this.animationStateChanged.emit(event);
    }
    startExitAnimation() {
        this.state = 'exit';
        this.changeDetectorRef.markForCheck();
    }
    ngOnDestroy() {
        // if state has a value 'enter' and the component is destroyed,
        // we should attempt to remove the overlay as well
        if (this.state === 'enter') {
            this.destroyedAbnormally$.next(void 0);
            this.destroyedAbnormally$.complete();
        }
    }
}
ModalContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalContainerComponent, deps: [{ token: i0.ElementRef }, { token: i1.FocusTrapFactory }, { token: i0.ChangeDetectorRef }, { token: i2.WindowService }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Component });
ModalContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: ModalContainerComponent, selector: "ds-modal-container", host: { listeners: { "@modalContainer.start": "onAnimationStart($event)", "@modalContainer.done": "onAnimationDone($event)" }, properties: { "class.ds-modal-container": "true", "class.d-flex": "true", "class.flex-column": "true", "class.align-items-center": "true", "class.justify-content-center": "true", "class.outline-none": "true", "class.mh-100": "true", "attr.role": "isAppleMobileDevice ? \"alertdialog\" : \"dialog\"", "attr.ds-modal-id": "id", "attr.id": "id", "@modalContainer": "state", "attr.tabindex": "0" } }, viewQueries: [{ propertyName: "componentOutlet", first: true, predicate: NgComponentOutlet, descendants: true }], ngImport: i0, template: "<ng-container\n  *ngIf=\"!isTemplate()\"\n  [ngComponentOutlet]=\"outletData.content\"\n  [ngComponentOutletInjector]=\"outletData.injector\"\n></ng-container>\n\n<ng-container\n  *ngIf=\"isTemplate()\"\n  [ngTemplateOutlet]=\"outletData.content\"\n  [ngTemplateOutletContext]=\"outletData.context\"\n></ng-container>\n", directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgComponentOutlet, selector: "[ngComponentOutlet]", inputs: ["ngComponentOutlet", "ngComponentOutletInjector", "ngComponentOutletContent", "ngComponentOutletNgModuleFactory"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], animations: [dsModalAnimations.modalContainer], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ds-modal-container', encapsulation: ViewEncapsulation.None, animations: [dsModalAnimations.modalContainer], host: {
                        '[class.ds-modal-container]': 'true',
                        '[class.d-flex]': 'true',
                        '[class.flex-column]': 'true',
                        '[class.align-items-center]': 'true',
                        '[class.justify-content-center]': 'true',
                        '[class.outline-none]': 'true',
                        '[class.mh-100]': 'true',
                        '[attr.role]': 'isAppleMobileDevice ? "alertdialog" : "dialog"',
                        '[attr.ds-modal-id]': 'id',
                        '[attr.id]': 'id',
                        '[@modalContainer]': 'state',
                        '(@modalContainer.start)': 'onAnimationStart($event)',
                        '(@modalContainer.done)': 'onAnimationDone($event)',
                        '[attr.tabindex]': '0',
                    }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container\n  *ngIf=\"!isTemplate()\"\n  [ngComponentOutlet]=\"outletData.content\"\n  [ngComponentOutletInjector]=\"outletData.injector\"\n></ng-container>\n\n<ng-container\n  *ngIf=\"isTemplate()\"\n  [ngTemplateOutlet]=\"outletData.content\"\n  [ngTemplateOutletContext]=\"outletData.context\"\n></ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.FocusTrapFactory }, { type: i0.ChangeDetectorRef }, { type: i2.WindowService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { componentOutlet: [{
                type: ViewChild,
                args: [NgComponentOutlet, { static: false }]
            }] } });

let uniqueId = 0;
class ModalRef {
    constructor(overlayRef, modalContainerInstance, windowService, id = `ds-modal-container-${uniqueId++}`) {
        this.overlayRef = overlayRef;
        this.modalContainerInstance = modalContainerInstance;
        this.windowService = windowService;
        this.id = id;
        this.afterOpened$ = new Subject();
        this.afterClosed$ = new Subject();
        this.beforeClosed$ = new Subject();
        modalContainerInstance.id = id;
        this.registerModalAnimationEnter();
        this.registerModalAnimationExit();
        this.registerOverlayEvents();
    }
    close(dialogResult) {
        this.result = dialogResult;
        this.modalContainerInstance.animationStateChanged
            .pipe(filter((event) => event.phaseName === 'start'), take(1))
            .subscribe(() => {
            this.beforeClosed$.next(this.result);
            this.beforeClosed$.complete();
        });
        this.modalContainerInstance.startExitAnimation();
        if (this.windowService.isBrowser()) {
            document.body.classList.remove('ds-no-overflow');
        }
    }
    afterOpened() {
        return this.afterOpened$.asObservable();
    }
    afterClosed() {
        return this.afterClosed$.asObservable();
    }
    beforeClosed() {
        return this.beforeClosed$.asObservable();
    }
    backdropClick() {
        return this.overlayRef.backdropClick();
    }
    keydownEvents() {
        return this.overlayRef.keydownEvents();
    }
    registerModalAnimationEnter() {
        return this.modalContainerInstance.animationStateChanged
            .pipe(filter((event) => event.phaseName === 'done' && event.toState === 'enter'), take(1))
            .subscribe((_) => {
            this.afterOpened$.next(void 0);
            this.afterOpened$.complete();
        });
    }
    registerModalAnimationExit() {
        this.modalContainerInstance.destroyedAbnormally$.subscribe(() => {
            setTimeout(() => {
                const config = this.overlayRef.getConfig();
                if (config && config.disposeOnNavigation === true) {
                    if (this.windowService.isBrowser()) {
                        document.body.classList.remove('ds-no-overflow');
                    }
                    this.overlayRef.detachBackdrop();
                    this.overlayRef.detach();
                }
            });
        });
        return this.modalContainerInstance.animationStateChanged
            .pipe(filter((event) => event.phaseName === 'done' && event.toState === 'exit'), take(1))
            .subscribe((_) => {
            this.overlayRef.detachBackdrop();
            this.overlayRef.detach();
        });
    }
    registerOverlayEvents() {
        this.overlayDetachments();
        this.overlayKeyboardEvent();
    }
    overlayKeyboardEvent() {
        this.overlayRef
            .keydownEvents()
            .pipe(filter((event) => (event['keyCode'] === ESCAPE ||
            event['code'] === '27' ||
            event['key'] === 'escape') &&
            this.closeOnEscape))
            .subscribe((event) => this.close({ $event: event }));
    }
    overlayDetachments() {
        this.overlayRef.detachments().subscribe(() => {
            this.afterClosed$.next(this.result);
            this.afterClosed$.complete();
            this.componentInstance = null;
            this.overlayRef.dispose();
        });
    }
}

class ModalService {
    constructor(overlay, injector, windowService, utilsService) {
        this.overlay = overlay;
        this.injector = injector;
        this.windowService = windowService;
        this.utilsService = utilsService;
        this.openModals = [];
        this.utilsService
            .getApplicationRoot()
            .pipe(first())
            .subscribe((root) => {
            this.appRoot = root;
        });
    }
    open(content, viewContainerRef, config, modalInjectableData) {
        const windowConfig = new OverlayConfig({
            hasBackdrop: true,
            disposeOnNavigation: this.getDisposeOnNavigationConfig(config),
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            backdropClass: ['cdk-overlay-dark-backdrop'].concat(config && config.backdropClass ? config.backdropClass : []),
            panelClass: ['ds-modalWindow']
                .concat(config && config.windowClass ? config.windowClass : [])
                .concat(config && config.position === 'fixed'
                ? ['-fixed']
                : config && config.position === 'fixed-full-page'
                    ? ['-fixed-full-page']
                    : []),
            positionStrategy: this.overlay
                .position()
                .global()
                .centerHorizontally()
                .centerVertically(),
        });
        const overlayRef = this.overlay.create(windowConfig);
        const modalContainerInstance = this.attachModalContainer(overlayRef, viewContainerRef, windowConfig);
        const modalRef = this.attachModalContent(content, modalContainerInstance, viewContainerRef, overlayRef, windowConfig, config, modalInjectableData);
        this.openModals.push(modalRef);
        if (this.windowService.isBrowser()) {
            if (!config || (config && !config.enableScroll)) {
                document.body.classList.add('ds-no-overflow');
            }
            const x = window.scrollX;
            const y = window.scrollY;
            if (this.windowService.isAppleMobileDevice()) {
                document.body.classList.add('position-fixed');
            }
            const sub$ = modalRef
                .afterClosed()
                .pipe(first())
                .subscribe(() => {
                if (this.windowService.isAppleMobileDevice()) {
                    document.body.classList.remove('position-fixed');
                    window.scrollTo(x, y);
                }
                if (this.appRoot) {
                    this.appRoot.location.nativeElement.setAttribute('aria-hidden', 'false');
                }
                sub$.unsubscribe();
            });
            if (this.appRoot) {
                this.appRoot.location.nativeElement.setAttribute('aria-hidden', 'true');
            }
        }
        return modalRef;
    }
    attachModalContainer(overlay, viewContainerRef, config) {
        const injector = Injector.create({
            providers: [
                {
                    provide: OverlayConfig,
                    useValue: config,
                },
            ],
            parent: this.injector,
        });
        const containerPortal = new ComponentPortal(ModalContainerComponent, viewContainerRef, injector);
        const containerRef = overlay.attach(containerPortal);
        return containerRef.instance;
    }
    attachModalContent(content, modalContainer, viewContainerRef, overlayRef, overlayConfig, dsConfig, modalInjectableData) {
        const modalRef = new ModalRef(overlayRef, modalContainer, this.windowService);
        modalRef.closeOnEscape =
            dsConfig && !dsConfig.closeOnEscape ? dsConfig.closeOnEscape : true;
        overlayRef.backdropClick().subscribe((o) => {
            if (!dsConfig || (dsConfig && dsConfig.backdropClick)) {
                modalRef.close();
            }
        });
        if (content instanceof TemplateRef) {
            modalContainer.attach({
                content: content,
                context: {
                    $implicit: {
                        dsConfig,
                        modalContainer,
                        modalInjectableData,
                    },
                },
            });
        }
        else {
            const injector = this.createInjector(overlayConfig, modalRef, modalContainer, viewContainerRef, modalInjectableData);
            modalRef.componentInstance = modalContainer.attach({
                content,
                injector,
            });
        }
        return modalRef;
    }
    createInjector(windowConfig, modalRef, modalContainer, viewContainerRef, modalInjectableData) {
        return Injector.create({
            providers: [
                {
                    provide: ModalContainerComponent,
                    useValue: modalContainer,
                },
                {
                    provide: ModalRef,
                    useValue: modalRef,
                },
                {
                    provide: OverlayConfig,
                    useValue: windowConfig,
                },
                {
                    provide: ModalInjectableData,
                    useValue: modalInjectableData || new ModalInjectableData(null),
                },
            ],
            parent: viewContainerRef.injector || this.injector,
        });
    }
    close(modal, modalResult) {
        if (!(modal instanceof ModalRef) && typeof modal !== 'string') {
            throw new Error(`modalId: string or modalRef is required, found ${typeof modal}`);
        }
        if (modal instanceof ModalRef) {
            modal.close(modalResult);
            this.removeModalRef(modal);
            return;
        }
        const ref = this.getModalRefById(modal);
        ref.close(modalResult);
        this.removeModalRef(ref);
        if (this.appRoot) {
            this.appRoot.location.nativeElement.setAttribute('aria-hidden', 'false');
        }
    }
    getModalRefById(id) {
        return this.openModals.find((ref) => ref.id === id);
    }
    removeModalRef(modalRef) {
        return this.openModals.splice(this.openModals.findIndex((i) => i.id === modalRef.id), 1);
    }
    getDisposeOnNavigationConfig(config) {
        if (!config || typeof config.disposeOnNavigation === 'undefined')
            return true;
        return config.disposeOnNavigation;
    }
}
ModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalService, deps: [{ token: i1$1.Overlay }, { token: i0.Injector }, { token: i2.WindowService }, { token: i2.UtilsService }], target: i0.ɵɵFactoryTarget.Injectable });
ModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Overlay }, { type: i0.Injector }, { type: i2.WindowService }, { type: i2.UtilsService }]; } });

class ModalComponent {
    constructor(modalService, elementRef, windowService, modalRef) {
        this.modalService = modalService;
        this.elementRef = elementRef;
        this.windowService = windowService;
        this.modalRef = modalRef;
        this._hasCloseButton = true;
        this.variantSpecificIcon = {
            error: 'error-filled',
            'system-error': 'error-filled',
            success: 'checkmark-filled',
            alert: 'warning-filled',
        };
        this.size = 'large';
        this.position = 'center';
        this.fullScroll = false;
        this.closeClick = new EventEmitter();
    }
    get variant() {
        return this._variant;
    }
    set variant(value) {
        this._variant = value;
        this.setModalIcon();
    }
    get icon() {
        return this._icon;
    }
    set icon(value) {
        this._icon = value;
        this.setModalIcon();
    }
    get hasCloseButton() {
        return this._hasCloseButton;
    }
    set hasCloseButton(closeButton) {
        this._hasCloseButton = coerceBooleanProperty(closeButton);
    }
    get modalId() {
        return this.containerElement.getAttribute('ds-modal-id');
    }
    get isAndroidOrIOSPhone() {
        return (this.windowService.isMobile() &&
            (this.windowService.isAppleMobileDevice() ||
                this.windowService.isAndroidDevice()));
    }
    ngOnInit() {
        if (this.hasCloseButton &&
            (!this.closeCaption || (this.closeCaption && this.closeCaption === ''))) {
            throw new Error('Close button caption is required for accessibility.');
        }
        this.setModalIcon();
        this.dispatchEvent();
    }
    ngAfterContentInit() {
        if (this.windowService.isBrowser()) {
            this.containerElement = this.findParentContainer(this.elementRef.nativeElement);
            if (this.containerElement) {
                this.containerElement.setAttribute('aria-label', this.heading);
            }
        }
    }
    ngOnDestroy() {
        this.dispatchEvent(true);
    }
    setModalIcon() {
        this.modalIcon =
            this.icon || this.variantSpecificIcon[this.variant] || null;
    }
    close(event) {
        this.closeClick.next(event);
        if (!event.defaultPrevented) {
            this.modalService.close(this.modalRef || this.modalId, {
                $event: event,
            });
        }
    }
    findParentContainer(element) {
        if (element.tagName === 'DS-MODAL-CONTAINER')
            return element;
        return this.findParentContainer(element.parentElement);
    }
    dispatchEvent(isClosed = false) {
        this.windowService.dispatchEvent(isClosed ? ModalEvents.open : ModalEvents.close, {
            closeCaption: this.closeCaption,
            icon: this.icon,
            heading: this.heading,
            size: this.size,
            variant: this.variant,
        });
    }
}
ModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalComponent, deps: [{ token: ModalService }, { token: i0.ElementRef }, { token: i2.WindowService }, { token: ModalRef, optional: true }], target: i0.ɵɵFactoryTarget.Component });
ModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: ModalComponent, selector: "ds-modal", inputs: { size: "size", variant: "variant", position: "position", fullScroll: "fullScroll", heading: "heading", icon: "icon", iconCaption: "iconCaption", hasCloseButton: "hasCloseButton", closeCaption: "closeCaption" }, outputs: { closeClick: "closeClick" }, host: { properties: { "class.ds-modal": "true", "class.d-flex": "true", "class.flex-column": "true", "class.ds-bgcolor-white": "true", "class.ds-shadow-high": "true", "class.pt-48": "true", "class.px-24": "true", "class.px-sm-40": "size !== \"fixed-full-page\"", "class.-small": "size === \"small\"", "class.-medium": "size === \"medium\"", "class.-large": "size === \"large\"", "class.-large-adaptive": "size === \"large-adaptive\"", "class.-full-width-adaptive": "size === \"full-width-adaptive\"", "class.-fixed-full-page": "size === \"fixed-full-page\"", "class.-success": "variant === \"success\"", "class.-alert": "variant === \"alert\"", "class.-error": "variant === \"error\"", "class.-system-error": "variant === \"system-error\"", "class.-full-scroll": "fullScroll", "class.-mobile-max-height": "isAndroidOrIOSPhone" } }, viewQueries: [{ propertyName: "closeButton", first: true, predicate: ["closeButton"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div\n  class=\"ds-modal__header d-flex align-items-start\"\n  [ngClass]=\"{\n    'flex-column align-items-center': variant && variant === 'system-error',\n    'mw-1200 w-100 mx-auto px-lg-24': size === 'fixed-full-page'\n  }\"\n>\n  <ds-icon\n    class=\"ds-modal__icon\"\n    [ngClass]=\"{\n      'mr-8': variant && variant !== 'system-error',\n      'mb-16': variant && variant === 'system-error'\n    }\"\n    [size]=\"variant && variant === 'system-error' ? '48' : '24'\"\n    *ngIf=\"modalIcon\"\n    [name]=\"modalIcon\"\n    [caption]=\"iconCaption\"\n  ></ds-icon>\n  <p\n    class=\"ds-modal__heading mb-24\"\n    [innerHTML]=\"heading\"\n    [ngClass]=\"{\n      'text-title-3': size !== 'fixed-full-page',\n      'text-overline': size === 'fixed-full-page'\n    }\"\n  ></p>\n  <button\n    class=\"ds-modal__closeButton\"\n    *ngIf=\"hasCloseButton\"\n    ds-button\n    variant=\"icon\"\n    [title]=\"closeCaption\"\n    (click)=\"close($event)\"\n    #closeButton\n  >\n    {{ closeCaption }}\n    <ds-icon [name]=\"'close'\"></ds-icon>\n  </button>\n</div>\n\n<div\n  class=\"ds-modal__wrapper d-flex flex-column h-100 px-24\"\n  [ngClass]=\"{\n    'px-sm-40': size !== 'fixed-full-page',\n    'align-items-center': size === 'fixed-full-page',\n    'pt-24 ds-border-top ds-brcolor-concrete':\n      !fullScroll &&\n      footerRef.children.length > 0 &&\n      bodyRef.children.length > 0\n  }\"\n>\n  <div\n    class=\"ds-modal__body px-24\"\n    [ngClass]=\"{\n      'px-sm-40': size !== 'fixed-full-page',\n      'mw-1200': size === 'fixed-full-page',\n      'pb-40': fullScroll\n    }\"\n    #bodyRef\n  >\n    <ng-content select=\"[body]\"></ng-content>\n  </div>\n\n  <div\n    class=\"ds-modal__fixedContent\"\n    [ngClass]=\"{\n      'mt-24 pt-24 ds-border-top ds-brcolor-concrete':\n        !fullScroll &&\n        footerRef.children.length > 0 &&\n        bodyRef.children.length > 0\n    }\"\n  >\n    <div\n      class=\"d-flex flex-column-reverse flex-shrink-0\"\n      [ngClass]=\"{\n        'w-100': size === 'fixed-full-page'\n      }\"\n    >\n      <div\n        class=\"ds-modal__legal ds-bgcolor-misty px-24\"\n        [ngClass]=\"{\n          'px-sm-40': size !== 'fixed-full-page'\n        }\"\n      >\n        <div\n          class=\"py-24\"\n          [ngClass]=\"{\n            'mw-1200 mx-auto w-100 px-lg-24': size === 'fixed-full-page'\n          }\"\n        >\n          <ng-content select=\"[legal]\"></ng-content>\n        </div>\n      </div>\n\n      <div\n        class=\"ds-modal__footer mb-24 mb-sm-40\"\n        [ngClass]=\"{\n          'mw-1200 mx-auto w-100 px-lg-24': size === 'fixed-full-page'\n        }\"\n        #footerRef\n      >\n        <ng-content select=\"[footer]\"></ng-content>\n      </div>\n    </div>\n  </div>\n</div>\n", components: [{ type: i4.IconComponent, selector: "ds-icon", inputs: ["name", "color", "caption", "ariaHidden", "size"] }, { type: i5.ButtonComponent, selector: "button[ds-button], a[ds-button]", inputs: ["variant", "size", "alignIcon", "img", "transparent", "disabled", "tabIndex", "a11yDescription"] }], directives: [{ type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None });
__decorate([
    Required()
], ModalComponent.prototype, "heading", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ds-modal', encapsulation: ViewEncapsulation.None, host: {
                        '[class.ds-modal]': 'true',
                        '[class.d-flex]': 'true',
                        '[class.flex-column]': 'true',
                        '[class.ds-bgcolor-white]': 'true',
                        '[class.ds-shadow-high]': 'true',
                        '[class.pt-48]': 'true',
                        '[class.px-24]': 'true',
                        '[class.px-sm-40]': 'size !== "fixed-full-page"',
                        '[class.-small]': 'size === "small"',
                        '[class.-medium]': 'size === "medium"',
                        '[class.-large]': 'size === "large"',
                        '[class.-large-adaptive]': 'size === "large-adaptive"',
                        '[class.-full-width-adaptive]': 'size === "full-width-adaptive"',
                        '[class.-fixed-full-page]': 'size === "fixed-full-page"',
                        '[class.-success]': 'variant === "success"',
                        '[class.-alert]': 'variant === "alert"',
                        '[class.-error]': 'variant === "error"',
                        '[class.-system-error]': 'variant === "system-error"',
                        '[class.-full-scroll]': 'fullScroll',
                        '[class.-mobile-max-height]': 'isAndroidOrIOSPhone',
                    }, template: "<div\n  class=\"ds-modal__header d-flex align-items-start\"\n  [ngClass]=\"{\n    'flex-column align-items-center': variant && variant === 'system-error',\n    'mw-1200 w-100 mx-auto px-lg-24': size === 'fixed-full-page'\n  }\"\n>\n  <ds-icon\n    class=\"ds-modal__icon\"\n    [ngClass]=\"{\n      'mr-8': variant && variant !== 'system-error',\n      'mb-16': variant && variant === 'system-error'\n    }\"\n    [size]=\"variant && variant === 'system-error' ? '48' : '24'\"\n    *ngIf=\"modalIcon\"\n    [name]=\"modalIcon\"\n    [caption]=\"iconCaption\"\n  ></ds-icon>\n  <p\n    class=\"ds-modal__heading mb-24\"\n    [innerHTML]=\"heading\"\n    [ngClass]=\"{\n      'text-title-3': size !== 'fixed-full-page',\n      'text-overline': size === 'fixed-full-page'\n    }\"\n  ></p>\n  <button\n    class=\"ds-modal__closeButton\"\n    *ngIf=\"hasCloseButton\"\n    ds-button\n    variant=\"icon\"\n    [title]=\"closeCaption\"\n    (click)=\"close($event)\"\n    #closeButton\n  >\n    {{ closeCaption }}\n    <ds-icon [name]=\"'close'\"></ds-icon>\n  </button>\n</div>\n\n<div\n  class=\"ds-modal__wrapper d-flex flex-column h-100 px-24\"\n  [ngClass]=\"{\n    'px-sm-40': size !== 'fixed-full-page',\n    'align-items-center': size === 'fixed-full-page',\n    'pt-24 ds-border-top ds-brcolor-concrete':\n      !fullScroll &&\n      footerRef.children.length > 0 &&\n      bodyRef.children.length > 0\n  }\"\n>\n  <div\n    class=\"ds-modal__body px-24\"\n    [ngClass]=\"{\n      'px-sm-40': size !== 'fixed-full-page',\n      'mw-1200': size === 'fixed-full-page',\n      'pb-40': fullScroll\n    }\"\n    #bodyRef\n  >\n    <ng-content select=\"[body]\"></ng-content>\n  </div>\n\n  <div\n    class=\"ds-modal__fixedContent\"\n    [ngClass]=\"{\n      'mt-24 pt-24 ds-border-top ds-brcolor-concrete':\n        !fullScroll &&\n        footerRef.children.length > 0 &&\n        bodyRef.children.length > 0\n    }\"\n  >\n    <div\n      class=\"d-flex flex-column-reverse flex-shrink-0\"\n      [ngClass]=\"{\n        'w-100': size === 'fixed-full-page'\n      }\"\n    >\n      <div\n        class=\"ds-modal__legal ds-bgcolor-misty px-24\"\n        [ngClass]=\"{\n          'px-sm-40': size !== 'fixed-full-page'\n        }\"\n      >\n        <div\n          class=\"py-24\"\n          [ngClass]=\"{\n            'mw-1200 mx-auto w-100 px-lg-24': size === 'fixed-full-page'\n          }\"\n        >\n          <ng-content select=\"[legal]\"></ng-content>\n        </div>\n      </div>\n\n      <div\n        class=\"ds-modal__footer mb-24 mb-sm-40\"\n        [ngClass]=\"{\n          'mw-1200 mx-auto w-100 px-lg-24': size === 'fixed-full-page'\n        }\"\n        #footerRef\n      >\n        <ng-content select=\"[footer]\"></ng-content>\n      </div>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: ModalService }, { type: i0.ElementRef }, { type: i2.WindowService }, { type: ModalRef, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { size: [{
                type: Input
            }], variant: [{
                type: Input
            }], position: [{
                type: Input
            }], fullScroll: [{
                type: Input
            }], heading: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconCaption: [{
                type: Input
            }], hasCloseButton: [{
                type: Input
            }], closeCaption: [{
                type: Input
            }], closeButton: [{
                type: ViewChild,
                args: ['closeButton', { static: true, read: ElementRef }]
            }], closeClick: [{
                type: Output
            }] } });

class ModalModule {
}
ModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, declarations: [ModalComponent, ModalContainerComponent], imports: [CommonModule,
        OverlayModule,
        PortalModule,
        ButtonModule,
        A11yModule,
        IconModule], exports: [ModalComponent, ModalContainerComponent] });
ModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, providers: [ModalService], imports: [[
            CommonModule,
            OverlayModule,
            PortalModule,
            ButtonModule,
            A11yModule,
            IconModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ModalComponent, ModalContainerComponent],
                    imports: [
                        CommonModule,
                        OverlayModule,
                        PortalModule,
                        ButtonModule,
                        A11yModule,
                        IconModule,
                    ],
                    exports: [ModalComponent, ModalContainerComponent],
                    providers: [ModalService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ModalComponent, ModalContainerComponent, ModalModule, ModalRef, ModalService, dsModalAnimations };
